import { useTheme } from '../../../hooks/useTheme';

interface BlogHeroProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  categories: { label: string; color: string }[];
  totalArticles: number;
}

export default function BlogHero({
  searchQuery,
  onSearchChange,
  activeCategory,
  onCategoryChange,
  categories,
  totalArticles,
}: BlogHeroProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  return (
    <section
      className="relative pt-32 pb-16 px-4 sm:px-6 overflow-hidden"
      style={{
        background: isLight
          ? 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%)'
          : 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 100%)',
      }}
    >
      {/* Decorative bg elements */}
      <div
        className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #00cfff 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      <div className="max-w-4xl mx-auto relative z-10 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase mb-6 font-body"
          style={{
            borderColor: '#00cfff40',
            backgroundColor: '#00cfff10',
            color: '#00cfff',
          }}
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-article-line text-xs" />
          </div>
          Recursos & Blog
        </div>

        {/* Title */}
        <h1 className={`font-heading text-4xl sm:text-5xl font-extrabold mb-4 leading-tight ${isLight ? 'text-navy-950' : 'text-white'}`}>
          Aprende sobre{' '}
          <span className="text-electric">e-CF y DGII</span>
        </h1>

        <p className={`font-body text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto ${isLight ? 'text-navy-700' : 'text-white/60'}`}>
          Artículos, guías y normativa actualizada sobre facturación electrónica
          en República Dominicana para que tu negocio siempre esté al día.
        </p>

        {/* Search */}
        <div className="relative max-w-lg mx-auto mb-8">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className={`ri-search-line text-sm ${isLight ? 'text-navy-400' : 'text-white/30'}`} />
          </div>
          <input
            type="text"
            placeholder="Buscar artículos sobre e-CF, DGII, facturación..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm font-body transition-colors duration-200 outline-none ${
              isLight
                ? 'bg-white border-slate-200 text-navy-800 placeholder-slate-400 focus:border-electric/50'
                : 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-electric/50'
            }`}
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
              style={{ color: '#00cfff' }}
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <button
            onClick={() => onCategoryChange('Todos')}
            className={`px-4 py-2 rounded-full text-xs font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap border ${
              activeCategory === 'Todos'
                ? 'bg-electric text-navy-950 border-electric'
                : isLight
                ? 'border-slate-200 text-navy-600 hover:border-electric/40 hover:text-electric'
                : 'border-white/10 text-white/60 hover:border-electric/40 hover:text-electric'
            }`}
          >
            Todos ({totalArticles})
          </button>
          {categories.map((cat) => (
            <button
              key={cat.label}
              onClick={() => onCategoryChange(cat.label)}
              className="px-4 py-2 rounded-full text-xs font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap border"
              style={{
                backgroundColor: activeCategory === cat.label ? cat.color : 'transparent',
                borderColor: activeCategory === cat.label ? cat.color : cat.color + '40',
                color: activeCategory === cat.label ? '#0a1628' : cat.color,
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
